import { ITEMS } from '../../explorer/src/config'
import { civConfig, UnifiedItem } from '../../explorer/src/types/data'
import { findClosestMatch, getItemByCanonicalName } from '../../explorer/src/query/utils'
import { CIVILIZATIONS } from '../../explorer/data/src/lib/config/civs'
import { PBGIDS } from './pbgids'

export const IGNORE_LIST = [
  'icons/races/common/units/cannon_4', // rubble spawn
  'icons/races/common/units/culverin_4', // rubble spawn
  'icons/races/common/units/trebuchet_3', // finalized field construction
  'icons/races/common/units/ram_2', // finalized field construction
  'icons/races/common/units/siegetower_2', // finalized field construction
  'icons/races/english/units/wynguard_army', // spwans as individual units?
  'icons/races/english/upgrades/longbow_mastery', // wynguard rangers
  'icons/races/common/abilities/charge', // wynguard raiders
  'icons/races/common/units/vip', // king
  'icons/races/malian/units/cattle',
  'icons/races/malian/abilities/poison',
  'icons/races/chinese/dynasties',
  'icons/unit_stat_settlements_trading_with',
]

const MAPPING = {
  'icons/races/common/abilities/arm_villagers': 'icons/races/rus/units/militia'
}

async function getItem<T extends ITEMS>(type: T, id: string | number) {
  const SDK = await import('../../explorer/data/src/sdk')
  if  (type == null && typeof(id) === "number")
    return SDK.Get(id)
  else
    return SDK[type].get(id)
}

const CIV_ABBR = Object.keys(CIVILIZATIONS);

const ID_MAP: {[key: string]: Record<ITEMS, Record<string, string>>} = {
  common: {
    [ITEMS.UNITS]: {
      ram: 'battering-ram',
      siegetower: 'siege-tower',
      manatarms_1: 'man-at-arms',
      manatarms_2: 'man-at-arms',
      manatarms_3: 'man-at-arms',
      manatarms_4: 'man-at-arms',

      general_ship: 'hulk',
      trade_cart: 'trader',
      transport: 'transport-ship',

      warship: 'carrack',
      war_galley: 'galley',
      fireship: 'demolition-ship',

      handcannon: 'handcannoneer',
      scorpion: 'springald',
      scorpion_3: 'springald',
      trebuchet_cw: 'counterweight-trebuchet',
      cannon: 'bombard',
    },
    [ITEMS.BUILDINGS]: {
      gristmill: 'mill',
      wheat_field: 'farm',
      gold_mining_camp: 'mining-camp',
      stone_gate: 'stone-wall-gate',
      tower: 'stone-wall-tower',
    },
    [ITEMS.TECHNOLOGIES]: {
      tree_fell_rate: 'forestry',

      wood_gather_1: 'double-broadax',
      wood_gather_2: 'lumber-preservation',
      wood_gather_3: 'crosscut-saw',

      gold_gather_1: 'specialized-pick',
      gold_gather_2: 'acid-distillation',
      gold_gather_3: 'cupellation',

      hunting_villager: 'survival-techniques',
      hunting_scout: 'professional-scouts',

      food_gather_1: 'horticulture',
      food_gather_2: 'fertilization',
      food_gather_3: 'precision-cross-breeding',

      silk_bowstring: 'textiles',

      ranged_damage_technology_1: 'steeled-arrow',
      ranged_damage_technology_2: 'balanced-projectiles',
      ranged_damage_technology_3: 'platecutter-point',

      ranged_armor_technology_1: 'iron-undermesh',
      ranged_armor_technology_2: 'wedge-rivets',
      ranged_armor_technology_3: 'angled-surfaces',

      melee_damage_technology_1: 'bloomery',
      melee_damage_technology_2: 'decarbonization',
      melee_damage_technology_3: 'damascus-steel',

      melee_armor_technology_1: 'fitted-leatherwork',
      melee_armor_technology_2: 'insulated-helm',
      melee_armor_technology_3: 'master-smiths',

      tithe_barn: 'tithe-barns',
      siege_engineers: 'siege-engineering',
      greased_axle: 'greased-axles',
      roller_shutter_trigger: 'roller-shutter-triggers',
      mathematics: 'geometry',
      architecture: 'court-architects',
      fire_arrows: 'incendiary-arrows',
      siege_wrapping: 'siege-works',

      demolition_ship: 'explosives',
      forecastle: 'extra-ballista',
      war_galley_cannon: 'chaser-cannons',
      fire_damage: 'incendiaries',
      bodkin_point: 'heated-shot',
      advanced_clinker: 'shipwrights',

      stone_outpost: 'fortify-outpost',
      arrowslit_emplacement: 'arrowslits',
      cannon: 'cannon-emplacement',
      springald: 'springald-emplacement',

      adjustable_crossbar: 'adjustable-crossbars',

      superior_transport_constructon: 'lightweight-beams'
    },
    [ITEMS.UPGRADES]: {
      manatarms_2: 'early-men-at-arms',
      manatarms_3: 'men-at-arms',
      manatarms_4: 'elite-men-at-arms',
      archer_3: 'veteran-archers',
      archer_4: 'elite-archers',
      crossbowman_4: 'elite-crossbowmen',
      horseman_2: 'horsemen',
      horseman_3: 'veteran-horsemen',
      horseman_4: 'elite-horsemen',
      lancer_3: 'veteran-knights-or-lancers',
      lancer_4: 'elite-knights-or-lancers',
      knight_4: 'elite-knights-or-lancers',
      spearman_2: 'hardened-spearmen',
      spearman_3: 'veteran-spearmen',
      spearman_4: 'elite-spearmen',
    },
  },
  ab: {
    [ITEMS.UNITS]: {
      mameluke_2: 'camel-rider',
      mameluke_3: 'camel-rider',
      mameluke_4: 'camel-rider',
    },
    [ITEMS.BUILDINGS]: {
      building_landmark_house_of_wisdom: 'house-of-wisdom',
      building_wonder_age4_spiral_tower: 'prayer-hall-of-uqba',
    },
    [ITEMS.TECHNOLOGIES]: {
      // Yap, they did really mess this one up
      culture_wing: 'economic-wing',
      trade_wing: 'culture-wing',
      economic_wing: 'trade-wing',
      military_wing: 'military-wing',
      mameluke_shields: 'camel-rider-shields',
      improved_deposits: 'improved-processing',
      camel_armor: 'camel-rider-shields',
      trader_gold_income: 'spice-roads',
      trader_armor: 'armored-caravans',
      improved_trade_rate: 'grand-bazaar',
      faith: 'proselytization',
      camel_armor_2: 'camel-rider-barding'
    },
    [ITEMS.UPGRADES]: {
      mamaluke_4: 'elite-camel-riders',
      camel_archer_3: 'veteran-camel-archers',
      camel_archer_4: 'elite-camel-archers',
      ghulam_upgrade: 'elite-ghulam',
    },
  },
  ay: {
    [ITEMS.UNITS]: {
    },
    [ITEMS.BUILDINGS]: {
    },
    [ITEMS.TECHNOLOGIES]: {
    },
    [ITEMS.UPGRADES]: {
    },
  },
  by: {
    [ITEMS.UNITS]: {
    },
    [ITEMS.BUILDINGS]: {
    },
    [ITEMS.TECHNOLOGIES]: {
    },
    [ITEMS.UPGRADES]: {
    },
  },
  ch: {
    [ITEMS.UNITS]: {
      repeater_crossbowman_2: 'zhuge-nu',
      repeater_crossbowman_3: 'zhuge-nu',
      repeater_crossbowman_4: 'zhuge-nu',
      firelancer_3: 'fire-lancer',
      firelancer_4: 'fire-lancer',
    },
    [ITEMS.BUILDINGS]: {
      building_landmark_age1_academy: 'imperial-academy',
      building_landmark_age2_clocktower: 'astronomical-clocktower',
      building_landmark_age1_spirit_way: 'spirit-way',
      building_landmark_age3_zhengyangmen: 'barbican-of-the-sun',
      building_landmark_age3_great_wall: 'great-wall-gatehouse',
      building_landmark_age2_palace: 'imperial-palace',
      building_wonder_age4_forbidden_city: 'enclave-of-the-emperor',
    },
    [ITEMS.TECHNOLOGIES]: {
      imperial_examinations: 'imperial-examinations',
      reusable_barrels: 'additional-barrels'
    },
    [ITEMS.UPGRADES]: {
      firelancer_4: 'elite-fire-lancers',
      manatarms_4: 'elite-palace-guards',
      crossbowman_3: 'veteran-zhuge-nu',
      crossbowman_4: 'elite-zhuge-nu',
    },
  },
  de: {
    [ITEMS.UNITS]: {
      tower_war_elephant: 'tower-elephant',
      ghazi_rider_2: 'ghazi-raider',
      ghazi_rider_3: 'ghazi-raider',
      ghazi_rider_4: 'ghazi-raider',
      handcannon_elephant: 'sultans-elite-tower-elephant',
    },
    [ITEMS.BUILDINGS]: {
      building_landmark_age1_landmark_quwwat_ul_islam: 'dome-of-the-faith', // both variations with and without 'landmark-' exist
      building_landmark_age1_landmark_qutub_minar: 'tower-of-victory',
      building_landmark_age1_quwwat_ul_islam: 'dome-of-the-faith',
      building_landmark_age1_qutub_minar: 'tower-of-victory',
      'building_landmark_age2_khiji-mosque': 'house-of-learning',
      building_landmark_age2_siri_fort: 'compound-of-the-defender',
      building_landmark_age3_madrasa_e_firoz: 'hisar-academy',
      building_landmark_age3_bijay_mandal_palace: 'palace-of-the-sultan',
      building_wonder_age4_agra_fort: 'great-palace-of-agra',
    },
    [ITEMS.TECHNOLOGIES]: {
      speed: 'swiftness',
      siege_elephants: 'howdahs',
      population_increase: 'reinforced-foundations',
      improved_sword_steel: 'honed-blades',
      elephant_armor: 'armored-beasts',
      spotters: 'lookout-towers',
      torch_armor: 'slow-burning-defenses',
    },
    [ITEMS.UPGRADES]: {
      ghazi_rider_upgrade_3: 'veteran-ghazi-raiders',
      ghazi_rider_upgrade_4: 'elite-ghazi-raiders',
    },
  },
  en: {
    [ITEMS.UNITS]: {},
    [ITEMS.BUILDINGS]: {
      building_wonder_age1_westminster_hall_eng: 'council-hall',
      building_wonder_age2_westminster_palace: 'kings-palace',
      building_wonder_age3_westminster_abbey_eng: 'abbey-of-kings',
      building_wonder_age1_white_tower: 'the-white-tower',
      building_wonder_age4_whitehall_palace_eng: 'wynguard-palace',
      building_wonder_age2_windsor_castle_eng: 'berkshire-palace',
      building_wonder_age4_canterbury_cathedral_eng: 'cathedral-of-st-thomas',
    },
    [ITEMS.TECHNOLOGIES]: {
      set_up_camp: 'setup-camp',
    },
    [ITEMS.UPGRADES]: {
      archer_3: 'veteran-longbowmen',
      archer_4: 'elite-longbowmen',
    },
  },
  fr: {
    [ITEMS.UNITS]: {},
    [ITEMS.BUILDINGS]: {
      'building-_landmark_age1_casernes_centrales': 'school-of-cavalry',
      'building-_landmark_age2_marche_de_la_ville': 'chamber-of-commerce',
      'building-_landmark_age1_hotel_de_ville_central': 'guild-hall',
      'building-_landmark_age3_ecole_de_pudre_a_canon': 'college-of-artillery',
      'building-_landmark_age2_la_chateau_rouge': 'red-palace', // fyi in-game age doesn't have to match file name age
      'building-_landmark_age3_le_grande_university': 'royal-institute',
      'building-_wonder_age4_la_grande_cathedral': 'notre-dame',
    },
    [ITEMS.TECHNOLOGIES]: {
      crossbow_drills: 'crossbow-stirrups',
      imperial_examinations: 'imperial-examination',
      cantled_saddle: 'cantled-saddles',
      bloodlines: 'royal-bloodlines',
      general_ship_gunpowder: 'long-guns',
    },
    [ITEMS.UPGRADES]: {
      lancer_3: 'veteran-royal-knights',
      lancer_4: 'elite-royal-knights',
      crossbowman_4: 'elite-arbaletriers',
    },
  },
  hr: {
    [ITEMS.UNITS]: {
      landskrecht_3: 'landsknecht',
      landskrecht_4: 'landsknecht',
    },
    [ITEMS.BUILDINGS]: {
      'building-_landmark_age1_palatine_chapel': 'aachen-chapel',
      building_landmark_age1_imperial_palace_of_paderborn: 'meinwerk-palace',
      building_landmark_age2_bamberg_cathedral: 'regnitz-cathedral',
      building_landmark_age2_nuremberg_castle: 'burgrave-palace',
      building_landmark_age3_hollenzollern_castle: 'palace-of-swabia',
      building_landmark_age3_eltz_castle: 'elzbach-palace',
      building_wonder_age4_glucksburg_castle: 'great-palace-of-flensburg',
    },
    [ITEMS.TECHNOLOGIES]: {
      manatarms_maces: 'heavy-maces',
      two_handed_weapons: 'two-handed-weapons',
      riveted_chain: 'riveted-chain-mail',
      reinforce_defences: 'reinforced-defenses',
      fire_armor: 'slate-and-stone-construction',
      inspired_infantry: 'inspired-warriors',
      scale_mail: 'riveted-chain-mail'
    },
    [ITEMS.UPGRADES]: {
      landskrecht_4: 'elite-landsknechte',
    },
  },
  hl: {
    [ITEMS.UNITS]: {
    },
    [ITEMS.BUILDINGS]: {
    },
    [ITEMS.TECHNOLOGIES]: {
    },
    [ITEMS.UPGRADES]: {
    },
  },
  ja: {
    [ITEMS.UNITS]: {
    },
    [ITEMS.BUILDINGS]: {
    },
    [ITEMS.TECHNOLOGIES]: {
    },
    [ITEMS.UPGRADES]: {
    },
  },
  je: {
    [ITEMS.UNITS]: {
    },
    [ITEMS.BUILDINGS]: {
    },
    [ITEMS.TECHNOLOGIES]: {
    },
    [ITEMS.UPGRADES]: {
    },
  },
  kt: {
    [ITEMS.UNITS]: {
    },
    [ITEMS.BUILDINGS]: {
    },
    [ITEMS.TECHNOLOGIES]: {
    },
    [ITEMS.UPGRADES]: {
    },
  },
  ma: {
    [ITEMS.UNITS]: {
      musofadi_2: 'musofadi-warrior',
      musofadi_3: 'musofadi-warrior',
      musofadi_4: 'musofadi-warrior',
      gunner: 'musofadi-gunner',
      general_ship: 'war-canoe',
      war_galley: 'hunting-canoe',
    },
    [ITEMS.BUILDINGS]: {
      building_landmark_age1_mansa_quarry: 'mansa-quarry',
      building_landmark_age1_saharan_trade_network: 'saharan-trade-network',
      building_landmark_age2_farimba_garrison: 'farimba-garrison',
      building_landmark_age2_grand_fulani_corral: 'grand-fulani-corral',
      building_landmark_age3_fort_of_the_huntress: 'fort-of-the-huntress',
      building_landmark_age3_griot_bara: 'griot-bara',
      building_wonder_age4_great_mosque_control_mal: 'great-mosque',
      building_cattle_ranch: 'cattle-ranch',
      building_pit_mine: 'pit-mine',
      building_toll_outpost: 'toll-outpost',
    },
    [ITEMS.TECHNOLOGIES]: {
      upgrade_banco_repairs_mal: 'banco-repairs',
      upgrade_naval_javelin_ships_mal: 'canoe-tactics',
      upgrade_sofa_farima_leadership_mal: 'farima-leadership',
      upgrade_stealth_healing_mal: 'local-knowledge',
      upgrade_donso_precision_training_mal: 'precision-training',
      upgrade_archer_poison_arrow_mal: 'poisoned-arrows',
      upgrade_sofa_armor_mal: 'imported-armor',
    },
    [ITEMS.UPGRADES]: {
      spearman_2: 'hardened-donso',
      spearman_3: 'veteran-donso',
      spearman_4: 'elite-donso',
      javelin_thrower_3: 'veteran-javelin-thrower',
      javelin_thrower_4: 'elite-javelin-thrower',
      warrior_scout_2: 'upgrade-warrior-scout',
      warrior_scout_3: 'veteran-warrior-scout',
      warrior_scout_4: 'elite-warrior-scout',
      sofa_3: 'veteran-sofa',
      sofa_4: 'elite-sofa',
      musofadi_3: 'veteran-musofadi',
      musofadi_4: 'elite-musofadi',
    },

  },
  mo: {
    [ITEMS.UNITS]: {
      horsearcher_2: 'mangudai',
      horsearcher_3: 'mangudai',
      horsearcher_4: 'mangudai',

      unit_khan_1_mon: 'khan',
      unit_khan_2_mon: 'khan',
      unit_khan_3_mon: 'khan',
      unit_khan_4_mon: 'khan',
    },
    [ITEMS.BUILDINGS]: {
      building_ovoo_mon: 'ovoo',
      building_wonder_age1_deer_stones_mon: 'deer-stones',
      building_wonder_age2_khara_khoto: 'steppe-redoubt',
      building_wonder_age2_karakorum: 'the-silver-tree',
      building_wonder_age4_stupa: 'the-white-stupa',
      building_wonder_age1_kurultai_mon: 'kurultai',
      building_wonder_age4_khanbaliq: 'khaganate-palace',
      building_wonder_age4_monument_of_the_great_khan: 'monument-of-the-great-khan',
    },
    [ITEMS.TECHNOLOGIES]: {
      faster_packing: 'superior-mobility',
      ortoo_network: 'yam-network',
      mangudai_archer_damage: 'siha-bow-limbs',
      signal_arrow_duration: 'whistling-arrows',
      stone_trade: 'stone-commerce',
      fire_damage: 'additional-torches',
      improved_production: 'monastic-shrines',
    },
    [ITEMS.UPGRADES]: {
      horsearcher_3: 'veteran-mangudai',
      horsearcher_4: 'elite-mangudai',
      knight_4: 'elite-lancers',
      keshik_upgrade_3: 'veteran-keshiks',
      keshik_upgrade_4: 'elite-keshiks'
    },
  },
  od: {
    [ITEMS.UNITS]: {
    },
    [ITEMS.BUILDINGS]: {
    },
    [ITEMS.TECHNOLOGIES]: {
    },
    [ITEMS.UPGRADES]: {
    },
  },
  ot: {
    [ITEMS.UNITS]: {
      jannisary_3: 'janissary',
      jannisary_4: 'janissary',
    },
    [ITEMS.BUILDINGS]: {
      building_landmark_age1_cifte_minareli_medrese_ott: 'twin-minaret-medrese',
      building_landmark_age1_han_caravanserai_ott: 'sultanhani-trade-network',
      building_landmark_age2_topkapi_palace_ott: 'istanbul-imperial-palace',
      building_landmark_age2_tophane_armory_ott: 'mehmed-imperial-armory',
      building_landmark_age3_kilitbahir_castle_ott: 'sea-gate-castle',
      building_landmark_age3_instanbul_observatory_ott: 'istanbul-observatory',
      building_wonder_age4_blue_mosque_ott: 'azure-mosque',
      building_military_school_ott: 'military-school',
    },
    [ITEMS.TECHNOLOGIES]: {
      upgrade_imperial_blacksmith_stockpile_ott: 'advanced-academy',
      upgrade_anatolian_hills_ott: 'anatolian-hills',
      upgrade_imperial_military_training_ott: 'fast-training',
      upgrade_imperial_monk_formation_ott: 'field-work',
      upgrade_naval_imperial_fleet_ott: 'imperial-fleet',
      jannisary_company: 'janissary-company',
      jannisary_guns: 'janissary-guns',
      upgrade_imperial_mehter_drums_ott: 'mether-drums',
      upgrade_imperial_military_campus_ott: 'military-campus',
      upgrade_imperial_manned_siege_ott: 'siege-crews',
      upgrade_imperial_trader_capacity_ott: 'trade-bags',
    },
    [ITEMS.UPGRADES]: {
      horseman_3: 'veteran-sipahi',
      horseman_4: 'elite-sipahi',
      jannisary_4: 'elite-janissary'
    },
  },
  ru: {
    [ITEMS.UNITS]: {
      horsearcher_2: 'horse-archer',
      horsearcher_3: 'horse-archer',
      horsearcher_4: 'horse-archer',

      war_galley: 'lodya-attack-ship',
    },
    [ITEMS.BUILDINGS]: {
      building_landmark_age1_golden_gate_vladimir: 'the-golden-gate',
      building_landmark_age1_novgorod_kremlin: 'kremlin',
      building_landmark_age2_trinity_lavra: 'abbey-of-the-trinity',
      building_landmark_age2_muscovy_trade_company: 'high-trade-house',
      building_landmark_age3_kremlin_armory: 'high-armory',
      building_landmark_age3_spassakaya_tower: 'spasskaya-tower',
      building_wonder_age4_saint_basils_cathedral: 'cathedral-of-the-tsar',
    },
    [ITEMS.TECHNOLOGIES]: {
      cavalry_sabres: 'knight-sabers',
      springald_banded_arms: 'banded-arms',
      streltsy_precision: 'mounted-training',
      advanced_clinker: 'clinker-construction',
    },
    [ITEMS.UPGRADES]: {
      knight_3: 'knights',
      knight_4: 'elite-knights',
    },
  },
  zx: {
    [ITEMS.UNITS]: {
    },
    [ITEMS.BUILDINGS]: {
    },
    [ITEMS.TECHNOLOGIES]: {
    },
    [ITEMS.UPGRADES]: {
    },
  },
  gol: {
    [ITEMS.UNITS]: {
    },
    [ITEMS.BUILDINGS]: {
    },
    [ITEMS.TECHNOLOGIES]: {
    },
    [ITEMS.UPGRADES]: {
    },
  },
  mac: {
    [ITEMS.UNITS]: {
    },
    [ITEMS.BUILDINGS]: {
    },
    [ITEMS.TECHNOLOGIES]: {
    },
    [ITEMS.UPGRADES]: {
    },
  },
  sen: {
    [ITEMS.UNITS]: {
    },
    [ITEMS.BUILDINGS]: {
    },
    [ITEMS.TECHNOLOGIES]: {
    },
    [ITEMS.UPGRADES]: {
    },
  },
  tug: {
    [ITEMS.UNITS]: {
    },
    [ITEMS.BUILDINGS]: {
    },
    [ITEMS.TECHNOLOGIES]: {
    },
    [ITEMS.UPGRADES]: {
    },
  },
}

const actionMap = {
  upgradeScoutForesterMon: 'technologies/professional-scouts',
  upgradeScoutForesterImprovedMon: 'technologies/professional-scouts-improved',

  upgradeEconVillagerHuntingGear1Mon: 'technologies/survival-techniques',
  upgradeEconVillagerHuntingGear1ImprovedMon: 'technologies/survival-techniques-improved',

  upgradeEconResourceWoodFellRate1Mon: 'technologies/forestry',
  upgradeEconResourceWoodFellRate1ImprovedMon: 'technologies/forestry-improved',

  upgradeEconResourceWoodHarvestRate2ImprovedMon: 'technologies/double-broadax-improved',
  upgradeEconResourceWoodHarvestRate3ImprovedMon: 'technologies/lumber-preservation-improved',
  upgradeEconResourceWoodHarvestRate4ImprovedMon: 'technologies/crosscut-saw-improved',
  upgradeEconResourceWoodHarvestRate2: 'technologies/double-broadax',
  upgradeEconResourceWoodHarvestRate3: 'technologies/lumber-preservation',
  upgradeEconResourceWoodHarvestRate4: 'technologies/crosscut-saw',

  upgradeEconResourceFoodHarvestRate2ImprovedMon: 'technologies/horticulture-improved',
  upgradeEconResourceFoodHarvestRate3ImprovedMon: 'technologies/fertilization-improved',
  upgradeEconResourceFoodHarvestRate4ImprovedMon: 'technologies/precision-cross-breeding-improved',
  upgradeEconResourceFoodHarvestRate2: 'technologies/horticulture',
  upgradeEconResourceFoodHarvestRate3: 'technologies/fertilization',
  upgradeEconResourceFoodHarvestRate4: 'technologies/precision-cross-breeding',

  upgradeEconResourceGoldHarvestRate2ImprovedMon: 'technologies/specialized-pick-improved',
  upgradeEconResourceGoldHarvestRate3ImprovedMon: 'technologies/shaft-mining-improved',
  upgradeEconResourceGoldHarvestRate4ImprovedMon: 'technologies/cupellation-improved',
  upgradeEconResourceGoldHarvestRate2: 'technologies/specialized-pick',
  upgradeEconResourceGoldHarvestRate3: 'technologies/shaft-mining',
  upgradeEconResourceGoldHarvestRate4: 'technologies/cupellation',

  upgradeUnitTownCenterWheelbarrow1Mon: 'technologies/wheelbarrow',
  upgradeUnitWheelbarrow1ImprovedMon: 'technologies/wheelbarrow-improved',

  upgradeTechUniversityBiologyMon: 'technologies/biology',
  upgradeTechUniversityBiologyImprovedMon: 'technologies/biology-improved',

  upgradeVillagerHealth: 'technologies/textiles',
  upgradeVillagerHealthImprovedMon: 'technologies/textiles-improved',

  upgradeWeaponFireDamageMon: 'technologies/additional-torches',
  upgradeWeaponFireDamageImprovedMon: 'technologies/additional-torches-improved',

  upgradeRaidStoneBountyMon: 'technologies/stone-bounty',
  upgradeRaidStoneBountyImprovedMon: 'technologies/stone-bounty-improved',

  upgradeRaidBountyMon: 'technologies/raid-bounty',
  upgradeRaidBountyImprovedMon: 'technologies/raid-bounty-improved',

  upgradeTraderSilkroadStoneMon: 'technologies/stone-commerce',
  upgradeTraderSilkroadStoneImprovedMon: 'technologies/stone-commerce-improved',

  upgradeReligiousMonasteryOvooMon: 'technologies/monastic-shrines',

  upgradeUnitTownCenterFasterPacking1Mon: 'technologies/superior-mobility',

  upgradeMangudaiWeaponDamageMon: 'technologies/siha-bow-limbs',
  upgradeMangudaiWeaponDamageImprovedMon: 'technologies/siha-bow-limbs-improved',

  upgradeKhanSignalArrowMon: 'technologies/whistling-arrows',
  upgradeKhanSignalArrowImprovedMon: 'technologies/whistling-arrows-improved',

  upgradeManatarmsEliteArmyTacticsMon: 'technologies/elite-army-tactics',
  upgradeManatarmsEliteArmyTacticsImprovedMon: 'technologies/elite-army-tactics-improved',

  upgradeSiegeWorksMon: 'technologies/siege-works',
  upgradeSiegeWorksImprovedMon: 'technologies/siege-works-improved',

  upgradeSiegeRollerTriggerMon: 'technologies/roller-shutter-triggers',
  upgradeSiegeRollerTriggerImprovedMon: 'technologies/roller-shutter-triggers-improved',

  upgradeSiegeWeaponSpeedMon: 'technologies/greased-axles',
  upgradeSiegeWeaponSpeedImprovedMon: 'technologies/greased-axles-improved',

  upgradeSiegeAdjustableCrossbarMon: 'technologies/adjustable-crossbars',
  upgradeSiegeAdjustableCrossbarImprovedMon: 'technologies/adjustable-crossbars-improved',

  upgradeSiegeEngineers: 'technologies/siege-engineering',
  upgradeImprovedSiegeEngineersMon: 'technologies/siege-engineering-improved',

  upgradeUnitReligiousHerbalMedicine4: 'technologies/herbal-medicine',
  upgradeUnitReligiousHerbalMedicine4ImprovedMon: 'technologies/herbal-medicine-improved',

  upgradeTechMilitaryAcademy: 'technologies/military-academy',
  upgradeTechMilitaryAcademyImprovedMon: 'technologies/military-academy-improved',
}

const TYPE_MAP = {
  buildings: ITEMS.BUILDINGS,
  units: ITEMS.UNITS,
  upgrades: ITEMS.TECHNOLOGIES,
  uprades: ITEMS.TECHNOLOGIES,
  abilities: ITEMS.TECHNOLOGIES,
}

const iconToItemCache = new Map<string, UnifiedItem>()

export async function getItemFromPbgid(pbgid?: number, civ: civConfig): Promise<UnifiedItem> {
  if (!pbgid)
    return undefined

  if(PBGIDS.ignore.includes(pbgid)) return null // skip

  let item = await getItem(null, pbgid)

  if (item?.civs.includes(civ.abbr)) return item

  if(PBGIDS.fallback[pbgid]) return getItemFromPbgid(PBGIDS.fallback[pbgid], civ)

  return undefined
}

export async function getItemFromIcon(icon: string, civ: civConfig): Promise<UnifiedItem> {
  if (iconToItemCache.has(icon)) return iconToItemCache.get(icon)

  const [_, category, race, itemType, name] = (MAPPING[icon] ?? icon).split('/')

  // We ignore units that are known to be weirds spawns, gaia or UI interface icons
  if (IGNORE_LIST.includes(icon)) return null
  if (['gaia', 'hud'].includes(category)) return null
  if (!name) return undefined

  let type = TYPE_MAP[itemType]
  if (!type) throw new Error(`Uknown type ${itemType} for ${icon}`)

  // Small workaround cause we view the Abbasid House Of Wisdom wings as buildings;
  // if (["trade_wing", "culture_wing", "military_wing", "economic_wing"].includes(name) && type == ITEMS.TECHNOLOGIES) type = ITEMS.BUILDINGS;

  // Search for statically defined mapping, first for the civ, the in common, if not fallback to stripping age and formatting to kebab case
  let id = ID_MAP[civ.abbr][type][name] ?? ID_MAP.common[type][name] ?? name.replaceAll('_', '-').replace(/-\d/g, '')

  try {
    let item = await getItem(type, id)

    // since the game calls upgrades and techs the same, we retry and find a unit upgrade of the same name
    if (!item && type == ITEMS.TECHNOLOGIES) {
      // Since game files don't distinguish between research techonologies and unit upgrades, we try to find a unit upgrade with the same name
      // Search for statically defined mapping, first for the civ, the in common, if not fallback to formatting to kebab case
      let upgradeId = ID_MAP[civ.abbr].upgrades[name] ?? ID_MAP.common.upgrades[name] ?? name.replaceAll('_', '-')
      item = await getItem(ITEMS.UPGRADES, upgradeId)
      console.log(upgradeId)
      if (item) {
        type = ITEMS.UPGRADES
        id = upgradeId
      }
    }

    // Verify the item returned is available for civ
    if (item?.civs.includes(civ.abbr)) return item

    // Else we look at similair units as configured in the explorer i.e. for chinese instead of 'man-at-arms' we look for 'palace-guard'
    const match = await findClosestMatch(type, id, civ)
    if (match) return match
    else return undefined
  } catch (e) {
    throw new Error(`Could not find suitable match for ${icon} for ${civ.name}`, e)
  }
}

export async function getItemFromAction(action: string, civ: civConfig): Promise<UnifiedItem> {
  const itemId = actionMap[action]
  if (itemId) {
    try {
      const item = await getItemByCanonicalName(itemId)
      if (item?.civs.includes(civ.abbr)) return item
      else return await findClosestMatch(TYPE_MAP[item.type], item.id, civ)
    } catch (e) {
      throw new Error(`Could not find item for ${action} using id ${itemId}`, e)
    }
  }
  else return getItemFromIcon(`icons/races/fake/upgrades/${camelCaseToSnakeCase(action)}`, civ);
}

function camelCaseToSnakeCase(string: string) {
  return string.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}
